import React, { useCallback, useMemo } from 'react'
import { FileResponse, FileSource } from '../interfaces/FileResponse'
import { useSheetSource } from './use-sheet-source';
import style from './sheet-item.module.css';
import mainStyles from '../../index.module.css'
import FileHelper from '../../helpers/filehelper';
import MovementButton, { MovementType } from './movement-button.component';
import { FlagIcon, ListPlusIcon } from '../icons/icons';
import ShareButton from './share-button.component';

export interface ImageItemProps {
    item: FileResponse,
    addButton?: AddButton,
    upButton?: MovementButtonProps,
    firstButton?: MovementButtonProps,
    downButton?: MovementButtonProps,
    lastButton?: MovementButtonProps,
    removeButton?: MovementButtonProps,
    // Lets the user flag a search result that is not a sheet, like the Telegram "Não é uma partitura" button
    reportButton?: ReportButton,
    // Shows a button to send the sheet to WhatsApp / Telegram
    shareable?: boolean,
    maxHeight?: number,
    // Called when an image (not a PDF) is clicked; return true when handled, false to download as usual
    onImageClick?: (item: FileResponse) => boolean,
}

export interface AddButton {
    position: AddButtonPosition,
    caption?: string,
    onClick?: (item: FileResponse) => Promise<any>
}

export interface ReportButton {
    onClick: (item: FileResponse) => void
}

export enum AddButtonPosition {
    High,
    Low
}

export interface MovementButtonProps {
    visible: boolean
    onClick?: (item: FileResponse) => Promise<any>
}



export default function SheetItem(props: ImageItemProps) {
    const sheetSource = useSheetSource(props.item);
    const { src: srcValue, extension, blob: blobContent, pdfLabel: aditionalInfo } = sheetSource;

    const addButtonCaption = props.addButton?.caption || 'Adicionar ao repertório';

    const onAddRepertoireClick = async () => {
        if (props.addButton && props.addButton.onClick)
            await props.addButton!.onClick(props.item);
    }

    const AddRepetoire = <div className={style.actionMenu}>
        <button type='button' className={mainStyles.buttonSecondary} onClick={onAddRepertoireClick}>
            <ListPlusIcon />
            {addButtonCaption}
        </button>
        {props.reportButton && <button type='button' className={mainStyles.buttonSecondary} onClick={() => props.reportButton!.onClick(props.item)}>
            <FlagIcon />
            Não é uma partitura
        </button>}
        {props.shareable && <ShareButton item={props.item} source={sheetSource} />}
    </div>

    const showImage = useMemo(() => srcValue && (extension !== 'pdf' || !navigator.pdfViewerEnabled), [srcValue, extension]);
    const showPdf = useMemo(() => srcValue && extension === 'pdf' && navigator.pdfViewerEnabled, [srcValue, extension]);

    const download = useCallback(() => {
        if (props.item.source !== FileSource.Dropbox) {
            window.open(props.item.address, '_blank', 'noopener,noreferrer');
            return;
        }

        if (blobContent)
            FileHelper.TryDownload(props.item.address, blobContent);
    }, [props.item.address, props.item.source, blobContent]);

    const onImageClick = useCallback(() => {
        const handled = !!props.onImageClick && extension !== 'pdf' && props.onImageClick(props.item);

        if (!handled)
            download();
    }, [props, extension, download]);

    const fitHeight = props.maxHeight !== undefined;

    return <div className={fitHeight ? `${style.card} ${style.fitContainer}` : style.card} style={{
        height: props.maxHeight
    }}>
        {props.addButton && props.addButton.position === AddButtonPosition.High && AddRepetoire}
        <div className={fitHeight ? style.fitContent : undefined}>
            {showImage && <div onClick={onImageClick} className={fitHeight ? style.fitDivImage : style.divImage}>
                <img className={style.image} src={srcValue} alt={props.item.address} />
            </div>}
            {showPdf && <object className={style.pdf} data={srcValue} type="application/pdf" aria-label={props.item.address} />}
            {aditionalInfo && <>
                <div className={style.pdfLabel}><label>{aditionalInfo}</label></div>
            </>}
            <div className={style.movementBar}>
                {props.upButton && props.upButton.visible && <MovementButton
                    buttonType={MovementType.Up}
                    onClick={props.upButton.onClick!}
                    current={props.item}
                />}
                {props.firstButton && props.firstButton.visible && <MovementButton
                    buttonType={MovementType.First}
                    onClick={props.firstButton.onClick!}
                    current={props.item}
                />}
                {props.removeButton && <MovementButton
                    buttonType={MovementType.Remove}
                    onClick={props.removeButton.onClick!}
                    current={props.item}
                />}
                {props.downButton && props.downButton.visible && <MovementButton
                    buttonType={MovementType.Down}
                    onClick={props.downButton.onClick!}
                    current={props.item}
                />}
                {props.lastButton && props.lastButton.visible && <MovementButton
                    buttonType={MovementType.Last}
                    onClick={props.lastButton.onClick!}
                    current={props.item}
                />}
            </div>
        </div>

        {props.addButton && props.addButton.position === AddButtonPosition.Low && AddRepetoire}
    </div>
}